import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { getClients, createClient } from '@/services/api';
import { toast } from 'sonner';
import { Search, Plus, Star, ChevronRight, Users, AlertCircle, MapPin } from 'lucide-react';

export default function ClientsPage() {
  const { isSubscribed } = useAuth();
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState({ first_name: '', last_name: '', location: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchClients();
  }, [isSubscribed]);

  const fetchClients = async (search = '') => {
    if (!isSubscribed) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const data = await getClients(search);
      setClients(data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      if (error.response?.status !== 403) {
        toast.error('Failed to load clients');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    await fetchClients(searchQuery);
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    
    if (!newClient.first_name.trim() || !newClient.last_name.trim()) {
      toast.error('Please enter client first and last name');
      return;
    }

    setCreating(true);
    try {
      const created = await createClient({
        first_name: newClient.first_name.trim(),
        last_name: newClient.last_name.trim(),
        location: newClient.location.trim() || null
      });
      setClients([created, ...clients]);
      setDialogOpen(false);
      setNewClient({ first_name: '', last_name: '', location: '' });
      toast.success('Client added successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create client';
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  if (!isSubscribed) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Card className="border-[#D97757] border-2 bg-[#D97757]/5">
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-12 h-12 text-[#D97757] mx-auto mb-4" />
              <h2 className="font-heading text-2xl font-semibold text-stone-900 mb-2">
                Subscription Required
              </h2>
              <p className="text-stone-600 mb-6">
                Activate your subscription to access client database.
              </p>
              <Link to="/subscription">
                <Button className="h-11 px-8 rounded-full bg-[#D97757] text-white hover:bg-[#C06040]">
                  Activate Subscription
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="clients-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-semibold text-stone-900">Clients</h1>
            <p className="text-stone-600 mt-1">Search and manage client profiles</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="h-11 px-6 rounded-full bg-[#D97757] text-white hover:bg-[#C06040]" data-testid="add-client-dialog-btn">
                <Plus className="w-5 h-5 mr-2" />
                Add New Client
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Add New Client</DialogTitle>
                <DialogDescription className="text-stone-500">
                  Add a new client to track and review their service history.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateClient} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    value={newClient.first_name}
                    onChange={(e) => setNewClient({ ...newClient, first_name: e.target.value })}
                    placeholder="Jane"
                    className="h-11 rounded-lg"
                    data-testid="new-client-firstname"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    value={newClient.last_name}
                    onChange={(e) => setNewClient({ ...newClient, last_name: e.target.value })}
                    placeholder="Smith"
                    className="h-11 rounded-lg"
                    data-testid="new-client-lastname"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Location (Optional)</Label>
                  <Input
                    id="location"
                    value={newClient.location}
                    onChange={(e) => setNewClient({ ...newClient, location: e.target.value })}
                    placeholder="Los Angeles, CA"
                    className="h-11 rounded-lg"
                    data-testid="new-client-location"
                  />
                  <p className="text-xs text-stone-500">City and state to help identify clients</p>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1 h-11 rounded-full"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 h-11 rounded-full bg-stone-900 text-white hover:bg-stone-800"
                    disabled={creating}
                    data-testid="create-client-btn"
                  >
                    {creating ? 'Adding...' : 'Add Client'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <Card className="mb-8 border-stone-100 shadow-sm" data-testid="client-search-card">
          <CardContent className="p-6">
            <form onSubmit={handleSearch} className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  type="text"
                  placeholder="Search by name or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-12 pl-10 rounded-lg border-stone-200"
                  data-testid="client-search-input"
                />
              </div>
              <Button 
                type="submit" 
                className="h-12 px-8 rounded-lg bg-stone-900 text-white hover:bg-stone-800"
                data-testid="client-search-btn"
              >
                Search
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Client List */}
        <Card className="border-stone-100 shadow-sm" data-testid="client-list-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-stone-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-stone-500" />
              All Clients ({clients.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="py-12 text-center text-stone-500">Loading clients...</div>
            ) : clients.length === 0 ? (
              <div className="py-12 text-center">
                <Users className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500 mb-4">No clients found</p>
                <Button 
                  onClick={() => setDialogOpen(true)}
                  className="h-10 px-6 rounded-full bg-[#D97757] text-white hover:bg-[#C06040]"
                >
                  Add First Client
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-stone-100">
                {clients.map((client) => (
                  <Link 
                    key={client.id}
                    to={`/clients/${client.id}`}
                    className="flex items-center justify-between p-5 hover:bg-stone-50 transition-colors group"
                    data-testid={`client-row-${client.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center">
                        <span className="text-stone-600 font-semibold text-lg">
                          {client.first_name.charAt(0)}{(client.last_name || client.last_initial || '')?.charAt(0) || ''}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-stone-900 text-lg">
                          {client.first_name} {client.last_name || (client.last_initial ? `${client.last_initial}.` : '')}
                        </p>
                        <div className="flex items-center gap-3 text-sm text-stone-500">
                          {client.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {client.location}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <div className="flex items-center gap-1">
                          <Star className={`w-5 h-5 ${client.avg_rating > 0 ? 'fill-[#D97757] text-[#D97757]' : 'text-stone-300'}`} />
                          <span className="font-semibold text-stone-900 text-lg">
                            {client.avg_rating > 0 ? client.avg_rating.toFixed(1) : '—'}
                          </span>
                        </div>
                        <p className="text-xs text-stone-500">{client.review_count} reviews</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-stone-400 group-hover:text-stone-600 transition-colors" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
